import { EAttributeUnit, EAttributeUnitFamily, IAttributeLocalization } from '../../@types/AttributeTypes';

export const attributeUnitDisplayMap: Record<EAttributeUnit, string> = {
  // Distance
  [EAttributeUnit.MM]: 'mm',
  [EAttributeUnit.CM]: 'cm',
  [EAttributeUnit.DM]: 'dm',
  [EAttributeUnit.M]: 'm',
  [EAttributeUnit.KM]: 'km',
  [EAttributeUnit.IN]: 'in',
  [EAttributeUnit.FT]: 'ft',
  [EAttributeUnit.YD]: 'yd',
  [EAttributeUnit.MI]: 'mi',

  // Area
  [EAttributeUnit.MM_2]: 'mm²',
  [EAttributeUnit.CM_2]: 'cm²',
  [EAttributeUnit.DM_2]: 'dm²',
  [EAttributeUnit.M_2]: 'm²',
  [EAttributeUnit.KM_2]: 'km²',
  [EAttributeUnit.IN_2]: 'in²',
  [EAttributeUnit.FT_2]: 'ft²',
  [EAttributeUnit.YD_2]: 'yd²',
  [EAttributeUnit.MI_2]: 'mi²',

  // Volume
  [EAttributeUnit.MM_3]: 'mm³',
  [EAttributeUnit.CM_3]: 'cm³',
  [EAttributeUnit.DM_3]: 'dm³',
  [EAttributeUnit.M_3]: 'm³',
  [EAttributeUnit.KM_3]: 'km³',
  [EAttributeUnit.IN_3]: 'in³',
  [EAttributeUnit.FT_3]: 'ft³',
  [EAttributeUnit.YD_3]: 'yd³',
  [EAttributeUnit.MI_3]: 'mi³',
  [EAttributeUnit.L]: 'L',
  [EAttributeUnit.IMP_GAL]: 'imp gal',
  [EAttributeUnit.US_GAL]: 'US gal',

  // Currency
  [EAttributeUnit.EUR]: '€',
  [EAttributeUnit.USD]: '$',
  [EAttributeUnit.GBP]: '£',
  [EAttributeUnit.TRY]: '₺',
  [EAttributeUnit.CHF]: '₣',

  // Sound & Light
  [EAttributeUnit.DECIBEL]: 'dB',
  [EAttributeUnit.CD_M2]: 'cd/m²',

  // Temperature
  [EAttributeUnit.TF]: '°F',
  [EAttributeUnit.TC]: '°C',

  // Angle
  [EAttributeUnit.DEGREE]: '°',

  // Misc
  [EAttributeUnit.PC]: '%',
  [EAttributeUnit.G_M3]: 'g/m³',
  [EAttributeUnit.M_S]: 'm/s',
  [EAttributeUnit.W_M2K]: 'W/m²K',
  [EAttributeUnit.M2K_W]: 'm²K/W',

  // Weight
  [EAttributeUnit.MG]: 'mg',
  [EAttributeUnit.G]: 'g',
  [EAttributeUnit.KG]: 'kg',
  [EAttributeUnit.T]: 't',
  [EAttributeUnit.OZ]: 'oz',
  [EAttributeUnit.LBS]: 'lbs',
  [EAttributeUnit.ST]: 'st',
  [EAttributeUnit.LT]: 'lt',

  // Energy
  [EAttributeUnit.KWH]: 'kWh',
};

const ALL_UNITS = Object.keys(attributeUnitDisplayMap) as EAttributeUnit[];

export const unitFamilyToUnitRecord: Record<EAttributeUnitFamily, EAttributeUnit[]> = {
  [EAttributeUnitFamily.DISTANCE]: [
    EAttributeUnit.MM,
    EAttributeUnit.CM,
    EAttributeUnit.DM,
    EAttributeUnit.M,
    EAttributeUnit.KM,
    EAttributeUnit.IN,
    EAttributeUnit.FT,
    EAttributeUnit.YD,
    EAttributeUnit.MI,
  ],

  [EAttributeUnitFamily.AREA]: [
    EAttributeUnit.MM_2,
    EAttributeUnit.CM_2,
    EAttributeUnit.DM_2,
    EAttributeUnit.M_2,
    EAttributeUnit.KM_2,
    EAttributeUnit.IN_2,
    EAttributeUnit.FT_2,
    EAttributeUnit.YD_2,
    EAttributeUnit.MI_2,
  ],

  [EAttributeUnitFamily.VOLUME]: [
    EAttributeUnit.MM_3,
    EAttributeUnit.CM_3,
    EAttributeUnit.DM_3,
    EAttributeUnit.M_3,
    EAttributeUnit.KM_3,
    EAttributeUnit.IN_3,
    EAttributeUnit.FT_3,
    EAttributeUnit.YD_3,
    EAttributeUnit.MI_3,
    EAttributeUnit.L,
    EAttributeUnit.IMP_GAL,
    EAttributeUnit.US_GAL,
  ],

  [EAttributeUnitFamily.CURRENCY]: [EAttributeUnit.EUR, EAttributeUnit.USD, EAttributeUnit.GBP, EAttributeUnit.TRY, EAttributeUnit.CHF],

  [EAttributeUnitFamily.SOUND_PRESSURE]: [EAttributeUnit.DECIBEL],
  [EAttributeUnitFamily.LUMINANCE]: [EAttributeUnit.CD_M2],
  [EAttributeUnitFamily.TEMPERATURE]: [EAttributeUnit.TF, EAttributeUnit.TC],
  [EAttributeUnitFamily.ANGLE]: [EAttributeUnit.DEGREE],
  [EAttributeUnitFamily.PERCENTAGE]: [EAttributeUnit.PC],
  [EAttributeUnitFamily.HUMIDITY]: [EAttributeUnit.G_M3],
  [EAttributeUnitFamily.VELOCITY]: [EAttributeUnit.M_S],
  [EAttributeUnitFamily.THERMAL_TRANSMITTANCE]: [EAttributeUnit.W_M2K, EAttributeUnit.M2K_W],

  [EAttributeUnitFamily.WEIGHT]: [
    EAttributeUnit.MG,
    EAttributeUnit.G,
    EAttributeUnit.KG,
    EAttributeUnit.T,
    EAttributeUnit.OZ,
    EAttributeUnit.LBS,
    EAttributeUnit.ST,
    EAttributeUnit.LT,
  ],

  [EAttributeUnitFamily.ENERGY]: [EAttributeUnit.KWH],

  [EAttributeUnitFamily.NONE]: ALL_UNITS,
};

export const getCorrespondingUnit = (unit: EAttributeUnit, toImperial: boolean): EAttributeUnit => {
  if (toImperial) {
    switch (unit) {
      // Distance
      case EAttributeUnit.MM:
      case EAttributeUnit.CM:
      case EAttributeUnit.DM:
        return EAttributeUnit.IN;
      case EAttributeUnit.M:
        return EAttributeUnit.FT;
      case EAttributeUnit.KM:
        return EAttributeUnit.MI;

      // Area
      case EAttributeUnit.MM_2:
      case EAttributeUnit.CM_2:
      case EAttributeUnit.DM_2:
        return EAttributeUnit.IN_2;
      case EAttributeUnit.M_2:
        return EAttributeUnit.FT_2;
      case EAttributeUnit.KM_2:
        return EAttributeUnit.MI_2;

      // Volume
      case EAttributeUnit.MM_3:
      case EAttributeUnit.CM_3:
      case EAttributeUnit.DM_3:
        return EAttributeUnit.IN_3;
      case EAttributeUnit.M_3:
        return EAttributeUnit.FT_3;
      case EAttributeUnit.KM_3:
        return EAttributeUnit.MI_3;
      case EAttributeUnit.L:
        return EAttributeUnit.US_GAL;

      // Temperature
      case EAttributeUnit.TC:
        return EAttributeUnit.TF;

      // Weight
      case EAttributeUnit.MG:
      case EAttributeUnit.G:
        return EAttributeUnit.OZ;
      case EAttributeUnit.KG:
        return EAttributeUnit.LBS;
      case EAttributeUnit.T:
        return EAttributeUnit.LT;

      default:
        return unit;
    }
  }

  switch (unit) {
    // Distance
    case EAttributeUnit.IN:
      return EAttributeUnit.CM;
    case EAttributeUnit.FT:
      return EAttributeUnit.M;
    case EAttributeUnit.YD:
      return EAttributeUnit.M;
    case EAttributeUnit.MI:
      return EAttributeUnit.KM;

    // Area
    case EAttributeUnit.IN_2:
      return EAttributeUnit.CM_2;
    case EAttributeUnit.FT_2:
      return EAttributeUnit.M_2;
    case EAttributeUnit.YD_2:
      return EAttributeUnit.M_2;
    case EAttributeUnit.MI_2:
      return EAttributeUnit.KM_2;

    // Volume
    case EAttributeUnit.IN_3:
      return EAttributeUnit.CM_3;
    case EAttributeUnit.FT_3:
      return EAttributeUnit.M_3;
    case EAttributeUnit.YD_3:
      return EAttributeUnit.M_3;
    case EAttributeUnit.MI_3:
      return EAttributeUnit.KM_3;
    case EAttributeUnit.IMP_GAL:
    case EAttributeUnit.US_GAL:
      return EAttributeUnit.L;

    // Temperature
    case EAttributeUnit.TF:
      return EAttributeUnit.TC;

    // Weight
    case EAttributeUnit.OZ:
      return EAttributeUnit.G;
    case EAttributeUnit.LBS:
    case EAttributeUnit.ST:
      return EAttributeUnit.KG;
    case EAttributeUnit.LT:
      return EAttributeUnit.T;

    default:
      return unit;
  }
};

export const getImperialToMetricFactor = (imperialUnit: EAttributeUnit): number => {
  const factor = IMPERIAL_TO_METRIC[imperialUnit];
  return factor !== undefined ? factor : 1;
};

export const getLocalizedAttributeLabel = (attr: { key: string; localization?: IAttributeLocalization }, locale: string): string => {
  const currentLocale = locale.split('-')[0];

  if (attr.localization) {
    return attr.localization[currentLocale] || attr.localization.en || attr.key;
  }

  return attr.key;
};

export const METRIC_TO_IMPERIAL: Partial<Record<EAttributeUnit, number>> = {
  // Distance
  [EAttributeUnit.MM]: 0.0393701,
  [EAttributeUnit.CM]: 0.393701,
  [EAttributeUnit.DM]: 3.93701,
  [EAttributeUnit.M]: 3.28084,
  [EAttributeUnit.KM]: 0.621371,

  // Area
  [EAttributeUnit.MM_2]: 0.0015500031,
  [EAttributeUnit.CM_2]: 0.15500031,
  [EAttributeUnit.DM_2]: 15.500031,
  [EAttributeUnit.M_2]: 10.7639,
  [EAttributeUnit.KM_2]: 0.386102,

  // Volume
  [EAttributeUnit.MM_3]: 0.0000610237,
  [EAttributeUnit.CM_3]: 0.0610237,
  [EAttributeUnit.DM_3]: 61.0237,
  [EAttributeUnit.M_3]: 35.3147,
  [EAttributeUnit.KM_3]: 2.39913e8,
  [EAttributeUnit.L]: 0.264172,

  // Temperature uses formula in convertAttributeValue
  [EAttributeUnit.TC]: 1,

  // Weight
  [EAttributeUnit.MG]: 0.000035274,
  [EAttributeUnit.G]: 0.035274,
  [EAttributeUnit.KG]: 2.20462,
  [EAttributeUnit.T]: 0.984207,

  // Energy
  [EAttributeUnit.KWH]: 1,
};

export const IMPERIAL_TO_METRIC: Partial<Record<EAttributeUnit, number>> = {
  // Distance
  [EAttributeUnit.IN]: 0.0254,
  [EAttributeUnit.FT]: 0.3048,
  [EAttributeUnit.YD]: 0.9144,
  [EAttributeUnit.MI]: 1609.344,

  // Area
  [EAttributeUnit.IN_2]: 0.00064516,
  [EAttributeUnit.FT_2]: 0.09290304,
  [EAttributeUnit.YD_2]: 0.83612736,
  [EAttributeUnit.MI_2]: 2.589988e6,

  // Volume
  [EAttributeUnit.IN_3]: 1.6387e-5,
  [EAttributeUnit.FT_3]: 0.0283168,
  [EAttributeUnit.YD_3]: 0.764555,
  [EAttributeUnit.MI_3]: 4.16818e9,
  [EAttributeUnit.US_GAL]: 3.78541,
  [EAttributeUnit.IMP_GAL]: 4.54609,

  // Temperature uses formula in convertAttributeValue
  [EAttributeUnit.TF]: 1,

  // Weight
  [EAttributeUnit.OZ]: 28.3495,
  [EAttributeUnit.LBS]: 0.453592,
  [EAttributeUnit.ST]: 6.35029,
  [EAttributeUnit.LT]: 1.01605,

  // Energy
  [EAttributeUnit.KWH]: 1,
};

export const METRIC_SCALE_FACTORS: Partial<Record<EAttributeUnit, number>> = {
  // Distance base: meter
  [EAttributeUnit.MM]: 0.001,
  [EAttributeUnit.CM]: 0.01,
  [EAttributeUnit.DM]: 0.1,
  [EAttributeUnit.M]: 1,
  [EAttributeUnit.KM]: 1000,

  // Area base: square meter
  [EAttributeUnit.MM_2]: 1e-6,
  [EAttributeUnit.CM_2]: 1e-4,
  [EAttributeUnit.DM_2]: 1e-2,
  [EAttributeUnit.M_2]: 1,
  [EAttributeUnit.KM_2]: 1e6,

  // Volume base: cubic meter, except L remains direct display/storage helper
  [EAttributeUnit.MM_3]: 1e-9,
  [EAttributeUnit.CM_3]: 1e-6,
  [EAttributeUnit.DM_3]: 1e-3,
  [EAttributeUnit.M_3]: 1,
  [EAttributeUnit.L]: 1,

  // Weight base: kilogram
  [EAttributeUnit.MG]: 0.000001,
  [EAttributeUnit.G]: 0.001,
  [EAttributeUnit.KG]: 1,
  [EAttributeUnit.T]: 1000,

  // Energy
  [EAttributeUnit.KWH]: 1,
};

export const convertAttributeValue = (value: number, fromUnit: EAttributeUnit, toImperialTarget: boolean): number => {
  // Celsius -> Fahrenheit
  if (fromUnit === EAttributeUnit.TC && toImperialTarget) {
    return value * 1.8 + 32;
  }

  // Fahrenheit -> Celsius
  if (fromUnit === EAttributeUnit.TF && !toImperialTarget) {
    return (value - 32) / 1.8;
  }

  const factor = toImperialTarget ? METRIC_TO_IMPERIAL[fromUnit] : IMPERIAL_TO_METRIC[fromUnit];

  if (factor !== undefined) {
    return value * factor;
  }

  return value;
};
